import { useEffect, useState, useCallback } from "react";
import { Switch } from "@progress/kendo-react-inputs";
import { fetchProfileData } from "./myprofileapi";
import { ICON_BASE_PATH } from "@/app/utils/config";
import AppDrawer from "@/app/models/drawer/AppDrawer";
import Dialogsmall from "@/app/models/dialogs/Dialogsmall";
import SecurityQuestions from "./SecurityQuestions";
import { SettingInfoLoading } from "@/app/components/webloading/shimmer/MyprofileLoading";
import GxSmallDropdown from "@/app/components/common/actions/dropdowns/GxSmallDropdown";
import { useT } from "@/app/hooks/useT";
import i18n from "@/i18n/i18n";
import { useDispatch, useSelector } from "react-redux";
import {
  setLanguage,
  setTimeZone,
  logSettingsState,
} from "@/app/redux/reducers/settingsSlice";
import type { RootState, AppDispatch } from "@/app/redux/store";
import timezones from "@/app/data/timezones.json";
import { useNavigate } from "react-router-dom";
import ReportIssuse from "../profilesettings/reportIssuse";
import Feedback from "../profilesettings/feedback";
import ChangePassword from "../profilesettings/changePassword";
import NotificationSubHeader from "./NotificationSubHeader";
import ContactUs from "../../contactUs/contactUs";
import UserprofileSuccessfully from "./userprofileSuccessfully";
import type {
  DashboardLayoutListPayload,
  DashboardLayoutListResponse,
  DashboardLayoutViewPayload,
  DashboardLayoutViewResponse,
} from "@/app/types/alterNativeDashboardLayout";
import {
  gxDashboardLayoutListApi,
  gxDashboardLayoutViewApi,
  gxuserfeedbackApi,
  gxuserreportApi,
} from "@/app/apiConfig/services/customerAbstractApiLayer";
import { DASHBOARD_LAYOUT_ROUTES } from "@/app/router/dashboardLayoutRoutes.config";
import { setDashboardLayoutView } from "@/app/redux/reducers/dashboardLayoutSlice";
import { gxIsArray } from "@/app/utils/helpers";
import {
  readStoredDashboardLayout,
  writeStoredDashboardLayout,
} from "@/app/utils/alterNativeDashboard/dashboardLayoutStorage";
import ComingSoon from "@/app/components/common/warning/comingSoon";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooter";
import InitialPage from "@/app/auth/landing/InitialPage";
import { ContactSupportContent } from "@/app/services/context/ContactSupportContent";

// Accordion Header Component
interface AccordionHeaderProps {
  icon: string;
  title: string;
  isExpanded: boolean;
  onClick: () => void;
}

const AccordionHeader = ({
  icon,
  title,
  isExpanded,
  onClick,
}: AccordionHeaderProps) => (
  <button
    className={`information-list clickable-row ${isExpanded ? "active-bg" : ""}`}
    onClick={onClick}
  >
    <div className="information-list-child">
      <div className="information-list-img">
        <img src={`${ICON_BASE_PATH}${icon}`} alt={title} />
      </div>
      <p>
        <span className="gx-p-sb">{title}</span>
      </p>
    </div>
    <div className={`information-downarrow ${isExpanded ? "rotate" : ""}`}>
      <img src={`${ICON_BASE_PATH}down_arrow.svg`} alt="Expand" />
    </div>
  </button>
);

export default function SettingsInfo() {
  const t = useT();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const selectedLanguage = useSelector(
    (state: RootState) => state.settings.language,
  );
  const selectedTimeZone = useSelector(
    (state: RootState) => state.settings.timeZone,
  );
  const userData = useSelector((state: RootState) => state.userData.data);
  const [settingsData, setSettingsData] = useState<any>(null);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showSubscriptionPlan, setShowSubscriptionPlan] = useState(false);
  const [isSecurityOpen, setSecurityOpen] = useState(false);
  const [isSuccessOpen, setSuccessOpen] = useState(false);
  const [successType, setSuccessType] = useState<"report" | "feedback">(
    "report",
  );
  const [isFaqOpen, setFaqOpen] = useState(false);
  const [isTutorialsOpen, setTutorialsOpen] = useState(false);
  const [isAppSupportOpen, setAppSupportOpen] = useState(false);
  const [isReportIssueOpen, setReportIssueOpen] = useState(false);
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);
  const [isPasswordOpen, setPasswordOpen] = useState(false);
  const [dashboardLayouts, setDashboardLayouts] = useState<
    DashboardLayoutListResponse[]
  >([]);
  const [selectedDashboardLayout, setSelectedDashboardLayout] =
    useState<string>("");
  const [dashboardLayoutLoading, setDashboardLayoutLoading] =
    useState<boolean>(true);
  const [dashboardLayoutError, setDashboardLayoutError] = useState<
    string | null
  >(null);

  // --- Dashboard layout VIEW (menu config) loading state ---
  const [isLayoutViewLoading, setIsLayoutViewLoading] =
    useState<boolean>(false);

  const timezoneOptions: string[] = [
    "Set Automatically",
    ...timezones.timezones.map(
      (tz) => `${tz.region} - ${tz.name} (UTC${tz.utc_offset})`,
    ),
  ];

  // Toggle Accordions
  const handleAccordionToggle = (section: string, currentState: boolean) => {
    switch (section) {
      case "preferences":
        setShowPreferences(!currentState);
        break;
      case "notifications":
        setShowNotifications(!currentState);
        break;
      case "security":
        setShowSecurity(!currentState);
        break;
      case "support":
        setShowSupport(!currentState);
        break;
      case "subscriptionPlan":
        setShowSubscriptionPlan(!currentState);
        break;
    }
  };

  // Security actions
  const handleSecurityAction = (label: string) => {
    if (label === "Change Password") {
      setPasswordOpen(true);
    } else setSecurityOpen(true);
  };

  // Support actions
  const handleSupportAction = (label: string) => {
    switch (label) {
      case "FAQs":
        setFaqOpen(true);
        break;
      case "Tutorials":
        setTutorialsOpen(true);
        break;
      case "Contact Us":
        setAppSupportOpen(true);
        break;
      case "Report an Issue":
        setReportIssueOpen(true);
        break;
      case "Feedback":
        setFeedbackOpen(true);
        break;
      case "About us":
        navigate("/aboutUs");
        break;
    }
  };
  // Language change
  const handleLanguageChange = useCallback(
    (value: string) => {
      dispatch(setLanguage(value));
      const langMap: Record<string, string> = {
        English: "en",
        Arabic: "ar",
        Spanish: "es",
        Catalan: "ca",
        Portuguese: "pt",
      };
      i18n.changeLanguage(langMap[value] || "en");
    },
    [dispatch],
  );

  const [changepassword, setchangepassword] = useState<() => void>(() => {});
  const [securityQuestions, setsecurityQuestions] = useState<() => void>(
    () => {},
  );

  const handlesecurityQuestions = () => {
    securityQuestions();
  };

  const handleChangePassword = () => {
    changepassword();
  };

  const handleTimeZoneChange = useCallback(
    (value: string) => {
      if (value === "Set Automatically")
        dispatch(setTimeZone("Set Automatically"));
      else {
        const selectedIndex = timezoneOptions.indexOf(value);
        if (selectedIndex > 0)
          dispatch(setTimeZone(timezones.timezones[selectedIndex - 1].id));
      }
    },
    [dispatch, timezoneOptions],
  );

  const getTimeZoneDisplayValue = () => {
    if (selectedTimeZone === "Set Automatically") return "Set Automatically";
    const tz = timezones.timezones.find((tz) => tz.id === selectedTimeZone);
    return tz
      ? `${tz.region} - ${tz.name} (UTC${tz.utc_offset})`
      : "Set Automatically";
  };

  const handleDashboardLayoutChange = useCallback(
    async (value: string) => {
      setSelectedDashboardLayout(value);

      const selected = dashboardLayouts.find(
        (layout) => layout.layoutName === value,
      );

      if (!selected) return;

      const targetRoute = DASHBOARD_LAYOUT_ROUTES[selected.layoutId];

      if (!targetRoute) {
        console.warn(
          `No route mapped for layoutId: ${selected.layoutId} (${selected.layoutName})`,
        );
        return;
      }

      setIsLayoutViewLoading(true);

      try {
        const payload: DashboardLayoutViewPayload = {
          layoutId: selected.layoutId,
        };
        const result = await gxDashboardLayoutViewApi(payload);

        const viewData = (
          gxIsArray(result?.data) ? result.data : []
        ) as DashboardLayoutViewResponse[];

        const layoutView = viewData[0];

        if (layoutView) {
          dispatch(
            setDashboardLayoutView({
              layoutId: layoutView.layoutId,
              layoutName: layoutView.layoutName,
              menuDetail: layoutView.menuDetail ?? [],
            }),
          );
        }
        writeStoredDashboardLayout({
          layoutId: selected.layoutId,
          layoutName: selected.layoutName,
        });

        navigate(targetRoute);
      } catch (err) {
      } finally {
        setIsLayoutViewLoading(false);
      }
    },
    [dashboardLayouts, navigate, dispatch],
  );

  const [report, setReport] = useState("");

  const handleReport = useCallback(async () => {
    if (!report) return;
    try {
      const payload = {
        userId: userData?.userId,
        description: report,
        groupSmeCompId: userData?.groupSmeCompId,
        channel: "WEB",
      };
      await gxuserreportApi(payload);
      handleSuccess("report");
    } catch (error) {}
  }, [report, userData]);

  const [feedback, setFeedback] = useState("");

  const handlefeedback = useCallback(async () => {
    if (!feedback) return;
    try {
      const payload = {
        userId: userData?.userId,
        description: feedback,
        groupSmeCompId: userData?.groupSmeCompId,
        channel: "WEB",
      };
      await gxuserfeedbackApi(payload);
      handleSuccess("feedback");
    } catch (error) {}
  }, [feedback, userData]);

  useEffect(() => {
    dispatch(logSettingsState());
  }, []);

  useEffect(() => {
    async function loadSettings() {
      const data = await fetchProfileData();
      setSettingsData(data.settings);
    }
    loadSettings();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadDashboardLayouts = async () => {
      setDashboardLayoutLoading(true);
      setDashboardLayoutError(null);

      try {
        const payload: DashboardLayoutListPayload = {
          searchText: "",
          pageNo: 1,
          pageSize: 5,
          sortBy: "",
          status: "",
        };
        const response = await gxDashboardLayoutListApi(payload);
        if (!isMounted) return;

        const rawLayouts = (response?.data ??
          []) as unknown as DashboardLayoutListResponse[];
        const layouts = [...rawLayouts].sort((a, b) => a.layoutId - b.layoutId);
        setDashboardLayouts(layouts);
        const storedLayout = readStoredDashboardLayout();
        const storedMatch = storedLayout
          ? layouts.find((layout) => layout.layoutId === storedLayout.layoutId)
          : undefined;

        const defaultLayout =
          storedMatch ??
          layouts.find((layout) => layout.layoutId === 1) ??
          layouts.find((layout) => layout.isMandatory === "Y") ??
          layouts[0];

        if (defaultLayout) {
          setSelectedDashboardLayout(defaultLayout.layoutName);
        }
      } catch {
        if (isMounted) {
          setDashboardLayoutError(t("dashboard_layout_fetch_error"));
        }
      } finally {
        if (isMounted) setDashboardLayoutLoading(false);
      }
    };

    loadDashboardLayouts();

    return () => {
      isMounted = false;
    };
  }, [t]);

  const dashboardLayoutOptions = dashboardLayouts.map(
    (layout) => layout.layoutName,
  );

  if (!settingsData)
    return (
      <>
        <div className="gx-profile-title">
          <h3 className="gx-h5-sb">{t("setting_information")}</h3>
        </div>
        <SettingInfoLoading />
      </>
    );

  const handleSuccess = (type: "feedback" | "report") => {
    if (type === "feedback") {
      setFeedbackOpen(false);
    }

    if (type === "report") {
      setReportIssueOpen(false);
    }

    setSuccessType(type);
    setSuccessOpen(true);
  };

  return (
    <>
      <div className="gx-profile-tab-container">
        <div className="gx-profile-title">
          <h3 className="gx-h3-b">{t("settings")}</h3>
        </div>

        {/* Preferences */}
        <AccordionHeader
          icon="preferance.svg"
          title={t("preferences")}
          isExpanded={showPreferences}
          onClick={() => handleAccordionToggle("preferences", showPreferences)}
        />
        <div
          className={`notification-container ${showPreferences ? "expanded" : "collapsed"}`}
        >
          {/* <h4 className="gx-p12-b">{t("Theme & Appearance")}</h4> */}
          {/* <div className="notification-list-child notification-border-none">
            <div className="notification-img-child">
              <div className="notification-img">
                <img src={`${ICON_BASE_PATH}themeicon.svg`} alt="Theme" />
              </div>
              <p className="gx-p14-md">{t("Theme")}</p>
            </div>
            <div className="notification-switch">
              <div className="gx-sunoutline">
                <img src={`${ICON_BASE_PATH}sunoutline.svg`} alt="Theme" />
                <Switch />
              </div>
            </div>
          </div> */}

          {/* ................................Dashboard................................... */}
          <div className="notification-list-child notification-border-none">
            <div className="notification-img-child">
              <div className="notification-img">
                <img
                  src={`${ICON_BASE_PATH}settingdashboard.svg`}
                  alt="Theme"
                />
              </div>
              <p className="gx-p14-md">{t("Dashboard")}</p>
            </div>
            <div className="notification-switch">
              {dashboardLayoutLoading ? (
                <span className="gx-p12-sb">{t("loading")}</span>
              ) : dashboardLayoutError ? (
                <span className="gx-p12-sb gx-error-text">
                  {dashboardLayoutError}
                </span>
              ) : (
                <GxSmallDropdown
                  value={selectedDashboardLayout}
                  onChange={handleDashboardLayoutChange}
                  options={dashboardLayoutOptions}
                />
              )}
            </div>
          </div>

          {/* ......................................... */}

          {/* <h4 className="gx-p12-b">{t("language_time")}</h4>
          <div className="notification-list-child notification-border-none">
            <div className="notification-img-child">
              <div className="notification-img">
                <img src={`${ICON_BASE_PATH}lang.svg`} alt="Language" />
              </div>
              <p className="gx-p14-md">{t("language")}</p>
            </div>
            <div className="notification-switch">
              <GxSmallDropdown
                value={selectedLanguage}
                onChange={handleLanguageChange}
                options={[
                  "English",
                  "Arabic",
                  "Spanish",
                  "Catalan",
                  "Portuguese",
                ]}
              />
            </div>
          </div> */}

          <div className="notification-list-child no-border">
            <div className="notification-img-child">
              <div className="notification-img">
                <img
                  src={`${ICON_BASE_PATH}time_zone.svg`}
                  alt={t("time_zone")}
                />
              </div>
              <p className="gx-p14-md">{t("time_zone")}</p>
            </div>
            <div className="notification-switch">
              <GxSmallDropdown
                value={getTimeZoneDisplayValue()}
                onChange={handleTimeZoneChange}
                options={timezoneOptions}
                searchable
              />
            </div>
          </div>
        </div>

        {/* Notifications */}
        <AccordionHeader
          icon="notifications.svg"
          title={t("notifications")}
          isExpanded={showNotifications}
          onClick={() =>
            handleAccordionToggle("notifications", showNotifications)
          }
        />
        <div
          className={`notification-container ${showNotifications ? "expanded" : "collapsed"}`}
        >
          <NotificationSubHeader userData={userData} />
        </div>

        {/* Security & Privacy */}
        <AccordionHeader
          icon="security&Privacy.svg"
          title={t("security_privacy")}
          isExpanded={showSecurity}
          onClick={() => handleAccordionToggle("security", showSecurity)}
        />
        <div
          className={`notification-container ${showSecurity ? "expanded" : "collapsed"}`}
        >
          <h4 className="gx-p14-md">{t("security_settings")}</h4>
          {settingsData.security.map(
            ({ label, type, icon }: any, index: number) => (
              <div
                className={`notification-list-child notification-border-none ${index === settingsData.security.length - 1 ? "no-border" : ""}`}
                key={label}
              >
                <div className="notification-img-child">
                  <div className="notification-img">
                    <img src={`${ICON_BASE_PATH}${icon}.svg`} alt={label} />
                  </div>
                  <p className="gx-p14-md">{label}</p>
                </div>
                <div className="notification-switch">
                  <button
                    onClick={() => handleSecurityAction(label)}
                    className="gx-p12-sb"
                  >
                    {type}
                  </button>
                </div>
              </div>
            ),
          )}
          <h4 className="gx-p14-md">{t("privacy_settings")}</h4>
          {settingsData.privacy.map(
            ({ label, enabled, icon }: any, index: number) => (
              <div
                className={`notification-list-child ${index === settingsData.privacy.length - 1 ? "no-border" : ""}`}
                key={label}
              >
                <div className="notification-img-child">
                  <div className="notification-img">
                    <img src={`${ICON_BASE_PATH}${icon}.svg`} alt={label} />
                  </div>
                  <p className="gx-p14-md">{label}</p>
                </div>
                <div className="notification-switch">
                  <Switch defaultChecked={enabled} />
                </div>
              </div>
            ),
          )}
        </div>

        {/* Support */}
        <AccordionHeader
          icon="support&Feedback.svg"
          title={t("support_settings")}
          isExpanded={showSupport}
          onClick={() => handleAccordionToggle("support", showSupport)}
        />
        <div
          className={`notification-container ${showSupport ? "expanded" : "collapsed"}`}
        >
          {settingsData.support.map(({ label, icon, type }: any) => (
            <div className="notification-list-child" key={label}>
              <div className="notification-img-child">
                <div className="notification-img">
                  <img src={`${ICON_BASE_PATH}${icon}.svg`} alt={label} />
                </div>
                <p className="gx-p14-md">{label}</p>
              </div>
              <div className="notification-switch">
                <button
                  onClick={() => handleSupportAction(label)}
                  className="gx-p12-sb"
                >
                  {type}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Manage Subscription Plan */}
        <AccordionHeader
          icon="subscriptionplanicon.svg"
          title={t("Manage_SubscriptionPlan")}
          isExpanded={showSubscriptionPlan}
          onClick={() =>
            handleAccordionToggle("subscriptionPlan", showSubscriptionPlan)
          }
        />
        <div
          className={`notification-container ${showSubscriptionPlan ? "expanded" : "collapsed"}`}
        >
          <>
            <div className="growx-company-box">
              <h4 className="gx-h4-sb">Change Your Subscription Plan</h4>

              <p className="gx-p12-m">
                If you would like to change your current subscription plan,
                please contact <strong> GrowX Global Support</strong> . Our
                support team will review your request, provide information about
                the available subscription options, and guide you through the
                subscription change process to ensure a smooth transition.{" "}
                <br />
              </p>

              <ContactSupportContent variant="inline" />
            </div>
          </>
        </div>
      </div>

      {/* Security Dialog */}
      <Dialogsmall
        isOpen={isSecurityOpen}
        handleClose={() => setSecurityOpen(false)}
        title={t("security")}
        subtitle={t("questions")}
        cancel={t("cancel")}
        submit={t("save_changes")}
        handleFooterSubmit={handlesecurityQuestions}
      >
        <SecurityQuestions
          onclose={setSecurityOpen}
          setsecurityQuestions={setsecurityQuestions}
        />
      </Dialogsmall>

      {/* Support Drawers */}
      <DrawerNoFooter
        title="FAQs"
        subtitle="Asked Questions"
        openDrawer={isFaqOpen}
        onClose={() => setFaqOpen(false)}
      >
        <ComingSoon submessage="FAQs will be available in a future update." />
      </DrawerNoFooter>

      <DrawerNoFooter
        title="Watch"
        subtitle="Tutorial videos"
        openDrawer={isTutorialsOpen}
        onClose={() => setTutorialsOpen(false)}
      >
        <ComingSoon submessage="Tutorial videos will be available in a future update." />
      </DrawerNoFooter>

      <DrawerNoFooter
        title=""
        subtitle="Contact Us"
        openDrawer={isAppSupportOpen}
        onClose={() => setAppSupportOpen(false)}
      >
        <ContactUs />
      </DrawerNoFooter>

      <AppDrawer
        title="Report "
        subtitle="An Issue"
        openDrawer={isReportIssueOpen}
        onClose={() => setReportIssueOpen(false)}
        submit={t("submit")}
        cancel={t("cancel")}
        onSubmit={handleReport}
      >
        <ReportIssuse setReport={setReport} report={report} />
      </AppDrawer>

      <AppDrawer
        title="Give us"
        subtitle="a feedback"
        openDrawer={isFeedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        submit={t("submit")}
        cancel={t("cancel")}
        onSubmit={handlefeedback}
      >
        <Feedback setFeedback={setFeedback} feedback={feedback} />
      </AppDrawer>

      {/* ..................Change Password..................... */}
      <AppDrawer
        title="Change"
        subtitle="Password"
        openDrawer={isPasswordOpen}
        onClose={() => setPasswordOpen(false)}
        submit={t("submit")}
        cancel={t("cancel")}
        onSubmit={handleChangePassword}
      >
        <ChangePassword
          setchangepassword={setchangepassword}
          onclose={setPasswordOpen}
        />
      </AppDrawer>

      <UserprofileSuccessfully
        isOpen={isSuccessOpen}
        onClose={() => setSuccessOpen(false)}
        type={successType}
      />

      {isLayoutViewLoading && (
        <div className="main-layout">
          <InitialPage />
        </div>
      )}
    </>
  );
}
